import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { StaticSRPHeader } from './StaticSRPHeader';
import { HitCompactView } from '../../../components/Hit/HitCompactView';
import { buildHitCompactViewPropsFromStatic } from './buildHitCompactViewProps';
import { SearchSidebar } from '../components/SearchSidebar';
import { SearchHeader } from '../components/SearchHeader';
import type { StaticSRPProps } from '../../../types/srp-static';
import type { Locale } from '../../../utils/routing/generate-url';
import { generateUrlFromIndexUiState } from '../../../utils/algolia/url-handling';
import { getIndexName } from '../../../constants';
import Footer from '../../../components/Footer';
import { TopEmployersStripWithClientFetch } from '../../../components/TopEmployersStripWithClientFetch';
import { TopRolesStripWithClientFetch } from '../../../components/TopRolesStripWithClientFetch';
import { EngagementStrip } from '../../../components/engagement/EngagementStrip';
import { SrpResultsGridSkeleton } from '../../../components/search/SrpResultsGridSkeleton';
import { matchSearchIndexPath } from '../match-search-index-path';
import { getLocationFromFiltersAndRoute } from '../utils/seoUtils';
import { getCategoryFromFiltersAndRoute } from '../../../utils/category/get-category-from-filters-and-route';
import { SRP_HITS_PER_PAGE } from '../../../utils/srp/srp-hits-per-page';
import { spliceResultsInterstitial } from '../../../utils/srp/srp-results-interstitial';
import { generateStatsText, showFinlandSubtitle } from '../../../utils/stats/generateStatsText';
import { getTranslatedLocationForHeader } from '../../../utils/location/translate-location-for-header';

/**
 * Number of job cards to fully render on the server; the rest are placeholders to reduce
 * render+serialize time. Kept equal to the SRP page size so the SSR HTML contains every job
 * on the page (crawlers see all cards, not placeholders).
 */
const INITIAL_SSR_HITS = SRP_HITS_PER_PAGE;

const noop = () => {};

/**
 * Static SRP: no SearchProvider, FacetsProvider, or SavedState.
 * All data from props. Renders same layout as interactive SRP to avoid CLS.
 * When initialResults is null and useProgressiveSrp is true, shows a simple loading message (slim payload case).
 */
export function StaticSRPPage({ data, locale, pathname }: StaticSRPProps): React.ReactElement {
  const intl = useIntl();
  const results = data.initialResults;
  const hits = results?.hits ?? [];
  const nbHits = results?.nbHits ?? 0;
  const page = data.initialPage ?? 0;
  const nbPages = results?.nbPages ?? 1;
  const hasNoResults = (data.hasNoResults ?? false) || nbHits === 0;

  const isSlimPayload = !results && (data as { useProgressiveSrp?: boolean }).useProgressiveSrp === true;

  const localeTyped = locale as Locale;
  const filtersObj = typeof data.initialFilters === 'string'
    ? (() => { try { return JSON.parse(data.initialFilters) as Record<string, string[]>; } catch { return {}; } })()
    : (data.initialFilters ?? {});

  const matched = matchSearchIndexPath(pathname);
  const locationForStrip = getLocationFromFiltersAndRoute(filtersObj, {
    routeParams: matched ? matched.routeParams : {},
  });
  const hideTopEmployersStrip = Boolean(locationForStrip) || data.initialLayout === 'map';
  const statsQuery = data.initialQuery ?? '';
  const categoryForHeader = getCategoryFromFiltersAndRoute(filtersObj, {
    routeParams: matched ? matched.routeParams : {},
  });
  // Real headline text for the (otherwise hookless) CustomStats <h1> below — without this the
  // stats row falls through to a plain, non-heading <p>, and on non-location pages that leaves
  // the whole shell without any <h1> at all (see healthcheck-smoke "Missing <h1>" failures).
  // Skipped when a category is active: CategoryHeader below already renders the page's <h1>,
  // and SearchSidebar's own hide-stats-row logic doesn't know about categories, so passing a
  // real statsText here would produce two <h1>s.
  const statsText = categoryForHeader
    ? undefined
    : generateStatsText({ nbHits, filters: filtersObj, query: statsQuery, locale: localeTyped });
  const showFinland = statsText ? showFinlandSubtitle(statsText, filtersObj, statsQuery) : false;
  // SearchSidebar hides its own stats row (and thus its <h1>) whenever a location is active,
  // deferring to MarketHeader's <h1> — but this static shell doesn't render MarketHeader (see
  // comment below), so location pages need their own <h1> to avoid ending up with none.
  const locationHeadingText = locationForStrip
    ? generateStatsText({
        nbHits,
        filters: { ...filtersObj, location: [getTranslatedLocationForHeader(locationForStrip, localeTyped)] },
        query: statsQuery,
        locale: localeTyped,
      })
    : null;
  // Same resolution +Page.tsx uses for the interactive SearchHeader — without this, the
  // static shell (the only thing many crawlers ever index, since SRPShell only swaps to the
  // interactive tree on user interaction/idle-callback) renders none of the curated
  // location/category H1, intro, FAQ, or internal-linking content. See TEC-420/421/422.

  const indexName = data.indexName ?? getIndexName(locale);
  const effectiveSort = data.initialSort ?? 'relevance';
  const currentRefinement =
    effectiveSort === 'date'
      ? `${indexName}_postedDate_desc`
      : effectiveSort === 'salary'
        ? `${indexName}_salaryRangeMin_desc`
        : indexName;
  const viewMode: 'list' | 'map' = data.initialLayout === 'map' ? 'map' : 'list';
  const hasActiveSearch = Boolean(data.initialQuery) || Object.keys(filtersObj).length > 0;
  const companyId =
    Array.isArray(filtersObj.companyId) && filtersObj.companyId.length > 0 ? filtersObj.companyId[0] : null;
  // In-feed (not above the header) — see spliceResultsInterstitial. Hidden on location pages,
  // in map view, and past page 1 (v1: only the first page of results carries the strip).
  const showTopEmployersStrip = !hideTopEmployersStrip && page === 0 && !isSlimPayload;
  const topEmployersInterstitial = showTopEmployersStrip ? (
    <TopEmployersStripWithClientFetch initialEmployers={data.topEmployersStrip} locale={locale} />
  ) : null;
  const topRolesInterstitial = showTopEmployersStrip ? (
    <TopRolesStripWithClientFetch initialRoles={data.topRolesStrip} locale={locale} />
  ) : null;

  const initialHits = hits.slice(0, INITIAL_SSR_HITS);
  const placeholderCount = Math.max(0, hits.length - INITIAL_SSR_HITS);

  return (
    <div className="relative flex flex-col h-screen mobile-viewport-height overflow-hidden lg:overflow-visible">
      <StaticSRPHeader
        locale={locale}
        query={data.initialQuery ?? ''}
        facetsData={data.facetsData as Record<string, Array<{ value: string; label?: string; count: number; isRefined?: boolean }>> | undefined}
        refinementList={filtersObj}
        pathname={pathname}
        indexName={data.indexName ?? getIndexName(locale)}
      />
      <div className="flex-1 flex lg:flex-row lg:relative lg:overflow-hidden min-h-0">
        <div
          data-srp-scroll-region=""
          className="flex-1 bg-[#F8F5F0] dark:bg-[#170F07] overflow-x-hidden overflow-y-auto min-h-0 overscroll-y-contain touch-pan-y [-webkit-overflow-scrolling:touch] pt-1 sm:pt-1 md:pt-2 pb-20 lg:pb-0 sm:px-0 md:px-2 lg:px-8"
        >
          <div className="w-full bg-[#F8F5F0] dark:bg-[#170F07] pt-1 sm:pt-1 md:pt-2">
            <div className="max-w-screen-2xl mx-auto sm:px-0 md:px-2 lg:px-8">
              <div>
              {/*
                Category branch only — SearchHeader's location branch renders MarketHeader,
                which calls useSearchFilters() (SearchContext) internally and crashes outside
                a SearchProvider. This static tree deliberately has no providers (see the file
                header comment). MarketHeaderStatic (components/MarketHeaderStatic.tsx) is the
                real no-context equivalent for locations, already used by SrpView's "static"
                mode (LayoutDefault.tsx's other static branch) — wiring it in here too needs
                its own data contract (marketHeaderStatic/staticStrings.marketHeader, built by
                buildSrpViewModel.ts) and is left as follow-up rather than rushed in. See
                TEC-420/421/422.
              */}
              {locationForStrip && !isSlimPayload && (
                <h1 className="sr-only">{locationHeadingText}</h1>
              )}
              <SearchHeader
                location={null}
                locale={localeTyped as unknown as 'en' | 'fi' | 'sv'}
                nbHits={nbHits}
                category={categoryForHeader}
                categoryHubData={data.categoryHubData}
              />
              <div className="ais-InstantSearch @container w-full min-w-0 flex flex-col gap-2 lg:flex-1">
                {!isSlimPayload && !hasNoResults ? (
                  <div className="px-2 @md:px-4 pb-2">
                    <EngagementStrip
                      feedbackSource="srp_static_shell"
                      variant="compact"
                      locale={locale}
                      feedbackMetadata={{
                        pathname,
                        query: data.initialQuery ?? '',
                        nbHits,
                      }}
                    />
                  </div>
                ) : null}
                <SearchSidebar
                  intl={intl}
                  viewMode={viewMode}
                  setViewMode={noop}
                  currentRefinement={currentRefinement}
                  handleSortChange={noop}
                  hasActiveSearch={hasActiveSearch}
                  location={locationForStrip}
                  companyId={companyId}
                  staticNbHits={isSlimPayload ? undefined : nbHits}
                  staticStatsText={isSlimPayload ? '' : statsText}
                  staticShowFinlandSubtitle={isSlimPayload ? false : showFinland}
                />

                <main id="job-results" className="job-results">
                  <section
                    className="srp-results"
                    aria-labelledby="results-title"
                  >
                    <h2 id="results-title" className="sr-only">
                      <FormattedMessage id="search.results.title" defaultMessage="Job Search Results" description="Screen reader heading for the job search results section" />
                    </h2>
                    {isSlimPayload ? (
                      <SrpResultsGridSkeleton />
                    ) : hasNoResults ? (
                      <div className="py-6 space-y-4">
                        <p className="text-[#7A6E5F] dark:text-[#B8A48C]">
                          <FormattedMessage id="search.noResults" defaultMessage="No jobs found." description="No results message" />
                        </p>
                        <EngagementStrip
                          feedbackSource="srp_static_no_results"
                          locale={locale}
                          feedbackMetadata={{
                            pathname,
                            query: data.initialQuery ?? '',
                            filters: filtersObj,
                          }}
                        />
                      </div>
                    ) : (
                      <ul className="srp-results-grid">
                        {spliceResultsInterstitial(
                          initialHits,
                          (hit, index) => (
                            <li
                              key={(hit.objectID ?? hit.id) as string ?? index}
                              className="srp-results-grid-item"
                            >
                            <HitCompactView
                              {...buildHitCompactViewPropsFromStatic(
                                hit as Record<string, unknown>,
                                index,
                                data,
                                localeTyped,
                                pathname
                              )}
                            />
                          </li>
                          ),
                          topEmployersInterstitial,
                          undefined,
                          topRolesInterstitial
                        )}
                      {Array.from({ length: placeholderCount }, (_, i) => (
                        <li
                          key={`placeholder-${initialHits.length + i}`}
                          className="srp-results-grid-item min-h-[150px]"
                          aria-hidden
                        />
                      ))}
                      </ul>
                    )}
                  </section>
                </main>

                {/* Minimal pagination: next/prev links (hidden when loading slim payload) */}
                {!isSlimPayload && nbPages > 1 && (() => {
                  const filtersObj = typeof data.initialFilters === 'string'
                    ? (() => { try { return JSON.parse(data.initialFilters) as Record<string, string[]>; } catch { return {}; } })()
                    : (data.initialFilters ?? {});
                  const prevUrl = page > 0
                    ? generateUrlFromIndexUiState(
                        { query: data.initialQuery ?? '', refinementList: filtersObj, page: page - 1 },
                        data.indexName ?? getIndexName(locale),
                        pathname
                      )
                    : null;
                  const nextUrl = page < nbPages - 1
                    ? generateUrlFromIndexUiState(
                        { query: data.initialQuery ?? '', refinementList: filtersObj, page: page + 1 },
                        data.indexName ?? getIndexName(locale),
                        pathname
                      )
                    : null;
                  return (
                    <nav className="flex items-center justify-center gap-4 py-2" aria-label="Pagination">
                      {prevUrl && (
                        <a href={prevUrl} className="text-sm font-medium text-[#3B5E45] dark:text-[#98CFA8] hover:underline">
                          ← Previous
                        </a>
                      )}
                      <span className="text-sm text-[#7A6E5F] dark:text-[#B8A48C]">
                        Page {page + 1} of {nbPages}
                      </span>
                      {nextUrl && (
                        <a href={nextUrl} className="text-sm font-medium text-[#3B5E45] dark:text-[#98CFA8] hover:underline">
                          Next →
                        </a>
                      )}
                    </nav>
                  );
                })()}
              </div>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>
      {/* Mobile bottom bar placeholder to reserve space */}
      <div className="lg:hidden h-14 flex-shrink-0 bg-[#F2EADF] dark:bg-[#2A1D0F]" aria-hidden />
    </div>
  );
}
